/**
 * StarRating — dual-mode (display + interactive) star row.
 *
 * Display mode (default): renders 5 stars filled up to `value`, no
 * interaction, keyboard-inert. Used inside ReviewCard.
 *
 * Interactive mode (`interactive` prop): stars are clickable buttons
 * and respond to keyboard (arrow keys, digits 1-5). Used inside the
 * submission form. Emits `onChange(value)` with the selected integer.
 *
 * Champagne accent is `#C9A86A`; empty stars are muted at #3a3831 so
 * they read as absent without competing with filled stars.
 */

import { Star } from "lucide-react";

export default function StarRating({
    value = 0,
    onChange = () => {},
    interactive = false,
    size = 22,
    "data-testid": testId,
    ariaLabel = "Rating",
    className = "",
}) {
    const stars = [1, 2, 3, 4, 5];

    const onKeyDown = (e) => {
        if (!interactive) return;
        if (e.key >= "1" && e.key <= "5") {
            e.preventDefault();
            onChange(parseInt(e.key, 10));
        } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
            e.preventDefault();
            onChange(Math.min(5, (value || 0) + 1));
        } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
            e.preventDefault();
            onChange(Math.max(1, (value || 1) - 1));
        }
    };

    if (interactive) {
        return (
            <div
                role="radiogroup"
                aria-label={ariaLabel}
                data-testid={testId}
                className={`inline-flex items-center gap-1.5 ${className}`}
                onKeyDown={onKeyDown}
                tabIndex={0}
            >
                {stars.map((n) => {
                    const active = n <= value;
                    return (
                        <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            aria-label={`${n} star${n === 1 ? "" : "s"}`}
                            onClick={() => onChange(n)}
                            data-testid={`${testId || "star-rating"}-${n}`}
                            className="p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A86A]/60 rounded-sm transition-transform duration-200 hover:scale-110"
                        >
                            <Star
                                size={size}
                                strokeWidth={1.4}
                                className={
                                    active
                                        ? "fill-[#C9A86A] text-[#C9A86A]"
                                        : "fill-transparent text-[#3a3831]"
                                }
                            />
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div
            aria-label={`${value} out of 5 stars`}
            data-testid={testId}
            className={`inline-flex items-center gap-1 ${className}`}
        >
            {stars.map((n) => (
                <Star
                    key={n}
                    size={size}
                    strokeWidth={1.4}
                    className={
                        n <= value
                            ? "fill-[#C9A86A] text-[#C9A86A]"
                            : "fill-transparent text-[#3a3831]"
                    }
                    aria-hidden="true"
                />
            ))}
        </div>
    );
}
